// client/src/hooks/useScopeKey.js
//
// ONE KEY THAT CHANGES WHEN THE SCOPE DOES.
//
// Switching the academic year or the phase changes what almost every page is
// meant to be showing, and most pages fetch once in a `useEffect(..., [])` and
// never again. About a dozen subscribe to `app:ay:changed` by hand; the rest
// keep whatever they loaded at mount — so the top bar reads 2026/2027 while the
// table underneath is still 2025/2026, and the only way to clear it is to
// re-enter a form or reload the browser.
//
// Rather than add a listener to every page one at a time, give the routed
// subtree a KEY. When it changes React unmounts the old page and mounts a fresh
// one: state discarded, mount effects re-run, every request reissued with the
// new headers. It is a reload of the page without reloading the app.
//
// Pages that already subscribe are unaffected — they get remounted instead of
// refetching in place, which ends in the same state.
//
// SEEDED FROM STORAGE AT MOUNT, not from a constant. Bootstrapping the scope for
// the first time writes the year and fires the event; if the key started empty
// that first resolution would count as a change and remount the whole app on
// first paint.

import { useEffect, useState } from 'react';
import { getCurrentAcademicYearId } from '../utils/academicYearContext';

const readPhase = () => {
  try {
    return (
      localStorage.getItem('schoolAdmin_activePhase') ||
      localStorage.getItem('activePhase') ||
      ''
    );
  } catch (_) {
    return '';
  }
};

// Read back from storage rather than trusting the event's `detail`: both
// emitters write storage before they dispatch, and storage is what fetchJson
// will actually put on the wire. Taking the value from the event would let the
// key and the request headers disagree.
const readScope = () => `${readPhase()}|${getCurrentAcademicYearId() ?? ''}`;

export default function useScopeKey() {
  const [key, setKey] = useState(readScope);

  useEffect(() => {
    const sync = () => setKey((held) => {
      const next = readScope();
      return next === held ? held : next;
    });

    window.addEventListener('app:ay:changed', sync);
    window.addEventListener('app:phase:changed', sync);
    return () => {
      window.removeEventListener('app:ay:changed', sync);
      window.removeEventListener('app:phase:changed', sync);
    };
  }, []);

  return key;
}
